import { useCallback, useMemo } from 'react'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import flowRuleApi from '@/api/config/flowRuleApi'
import { queryKeys } from '@/constant/common'
import { usePermission } from '@/hooks/permissions'
import { UpdateFlowRule } from '@/types/hydraulic'
import useSearchFlowRuleId from '@/features/config/hooks/hydraulic/flowRule/useSearchFlowRuleId'
import useSearchFlowSensor from '@/features/config/hooks/hydraulic/flowSensor/useSearchFlowSensor'
import useSearchFlowValve from '@/features/config/hooks/hydraulic/flowValve/useSearchFlowValve'
// The sensor IO picker lists the interface's INPUT and SDI-12 decoders — what a
// flow sensor can be wired to.
import useSensorDecoderOptions from '@/features/config/hooks/hydraulic/flowSensor/useSensorDecoderOptions'
import useGetDetailInput from '@/features/config/hooks/hydraulic/flowValve/useGetDetailInput'
import useIsControlHub from '@/features/config/hooks/controller/useIsControlHub'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import RuleRail, { RuleSection } from './RuleRail'
import { useRuleCopy } from './use-rule-copy'
import SensorsGrid from './SensorsGrid'
import ValvesGrid from './ValvesGrid'
import useSearchStation from '@/features/config/hooks/station/useSearchStation'
import { buildPurposeIdByName, decoderKeyOf } from './valveDecoderOptions'
import { useSearchDecoderType } from '@/features/config/hooks/controller'

interface FlowRulePanelProps {
  interfaceId: number
  pipeId: number
  flowRuleId: number
  /** Metric l/m, so a new valve can default to the pipe it feeds. */
  pipeCapacity?: number
  /** Which chunk to show — the pipe row opens one at a time. */
  section: RuleSection
  /** Refetches the pipe list so its rule / sensor / valve counts stay true. */
  onRefetchPipes?: () => Promise<unknown> | void
  /** Closes the hosting popover — Cancel, and a successful rule save. */
  onClose?: () => void
  /** The valves table needs far more width than the cards that replace it. */
  onValvesEditModeChange?: (editing: boolean) => void
  /** SCRUM-1032 QC item 6 — staged edits the popover must not lose. */
  onValvesDirtyChange?: (dirty: boolean) => void
  onSensorsDirtyChange?: (dirty: boolean) => void
}

/**
 * One chunk of a pipe's flow rule, expanded under its row: the alarm, the
 * learning settings, the sensors or the valves. Splitting the old full-form
 * panel into chunks keeps each expansion small enough to read at a glance —
 * and the row's four buttons say exactly what lives where. Rule deletion lives
 * on the pipe row now, next to where the rule was created.
 */
const FlowRulePanel = ({
  interfaceId,
  pipeId,
  flowRuleId,
  pipeCapacity,
  section,
  onRefetchPipes,
  onClose,
  onValvesEditModeChange,
  onValvesDirtyChange,
  onSensorsDirtyChange,
}: FlowRulePanelProps) => {
  const { isViewOnly } = usePermission()
  const copy = useRuleCopy()
  const queryClient = useQueryClient()

  const { data: rule, isLoading, refetch: refetchRule } = useSearchFlowRuleId(
    interfaceId,
    flowRuleId,
  )
  const {
    data: sensorData,
    isLoading: isLoadingSensors,
    refetch: refetchSensors,
  } = useSearchFlowSensor(interfaceId)
  const {
    data: valveData,
    isLoading: isLoadingValves,
    refetch: refetchValves,
  } = useSearchFlowValve(interfaceId)
  const { data: sensorOptions } = useSensorDecoderOptions(interfaceId)
  const { data: valveOptions } = useGetDetailInput(interfaceId)
  // A decoder already driving a station cannot also drive a valve, so the
  // picker leaves those out. `PageSize: 0` asks for the whole list — the check
  // has to cover every station, not the first page of them.
  const { data: stationData } = useSearchStation({ PageSize: 0 }, interfaceId)
  // Purpose ids come from the controller rather than from a constant: the
  // `OutputDecoderType` enum no longer matches what the API answers.
  const { data: decoderTypeData } = useSearchDecoderType({
    PageSize: 100,
    interfaceId,
  })
  const purposeIdByName = useMemo(
    () => buildPurposeIdByName(decoderTypeData?.list),
    [decoderTypeData],
  )

  const stationDecoderKeys = useMemo(() => {
    // `useSearchStation` hands back `false` — not an empty array — when the
    // controller has no stations, so `?? []` does not catch it.
    const stations = Array.isArray(stationData?.list) ? stationData!.list : []
    return new Set(
      stations.map((station: any) =>
        decoderKeyOf({ address: station.address, index: station.index }),
      ),
    )
  }, [stationData])
  // Only a control hub has sub-controllers, so only there does naming the
  // controller tell a decoder apart from its namesake on another one.
  const { data: isControlHub } = useIsControlHub(interfaceId)
  // A sensor's reading is stamped in the controller's own local time, so the
  // site's timezone — not the viewer's — is what says how old it is.
  const { data: interfaceDetail } = useSearchInterfaceId(String(interfaceId))

  // Both endpoints return every sensor / valve on the interface, so the rule's
  // own are picked out here. Worth a server-side filter when the API grows one.
  const sensors = useMemo(
    () => (sensorData?.list ?? []).filter((sensor) => sensor.flowRuleId === flowRuleId),
    [sensorData, flowRuleId],
  )
  const valves = useMemo(
    () => (valveData?.list ?? []).filter((valve) => valve.flowRuleId === flowRuleId),
    [valveData, flowRuleId],
  )

  const refreshAll = useCallback(async () => {
    await Promise.all([refetchRule(), refetchSensors(), refetchValves()])
    await onRefetchPipes?.()
  }, [onRefetchPipes, refetchRule, refetchSensors, refetchValves])

  const handleSaveRule = useCallback(
    async (values: UpdateFlowRule) => {
      try {
        await flowRuleApi.update(values)
        queryClient.invalidateQueries([queryKeys.flowRule])
        await refreshAll()
        onClose?.()
      } catch (error: any) {
        toast.error(error?.response?.data?.Message ?? copy.saveRuleFailed)
      }
    },
    [copy, onClose, queryClient, refreshAll],
  )

  if (isLoading) {
    return (
      <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '120px' }}>
        <CircularProgress size={22} />
      </Stack>
    )
  }

  if (section === 'alarm' || section === 'learning') {
    return (
      <RuleRail
        rule={rule ? { ...rule, pipeId } : rule}
        isViewOnly={isViewOnly}
        onSave={handleSaveRule}
        onCancel={onClose}
      />
    )
  }

  return (
    <Box sx={{ width: '100%', p: '14px 16px 16px' }}>
      {section === 'sensors' ? (
        <SensorsGrid
          interfaceId={interfaceId}
          flowRuleId={flowRuleId}
          sensors={sensors}
          // Every sensor on the interface, not just this rule's: a decoder
          // already wired to ANY pipe's rule is spoken for and must not be
          // offered again.
          takenSensors={sensorData?.list ?? []}
          options={sensorOptions ?? []}
          showController={Boolean(isControlHub)}
          timeZone={interfaceDetail?.timeZoneOriginId}
          loading={isLoadingSensors}
          isViewOnly={isViewOnly}
          onChanged={refreshAll}
          onDirtyChange={onSensorsDirtyChange}
        />
      ) : (
        <ValvesGrid
          interfaceId={interfaceId}
          flowRuleId={flowRuleId}
          valves={valves}
          sensors={sensors}
          pipeCapacity={pipeCapacity}
          loading={isLoadingValves}
          options={valveOptions ?? []}
          stationDecoderKeys={stationDecoderKeys}
          purposeIdByName={purposeIdByName}
          showController={Boolean(isControlHub)}
          isViewOnly={isViewOnly}
          onChanged={refreshAll}
          onEditModeChange={onValvesEditModeChange}
          onDirtyChange={onValvesDirtyChange}
        />
      )}
    </Box>
  )
}

export default FlowRulePanel
